import i18n from '@/i18n';
import { PlanningSidebarColor } from '@/modules/companyNeed/views/details/missions/types';
import { WorkerMissionStatus } from '@/types/mission';

import { blueBase, green700, neutral900, redBase, yellow700 } from '@/color';

export enum WorkerMissionStatusModifier {
	BLUE = 'blue',
	GREEN = 'green',
	YELLOW = 'yellow',
	RED = 'red',
	GREY = 'grey',
}

export const getWorkerMissionStatusLabel = (
	status: WorkerMissionStatus,
	count?: number
): string =>
	i18n.global.t(`page.companyNeed.missions.workerStatus.${status}`, { count });

export const getWorkerMissionStatusModifier = (
	status: WorkerMissionStatus
): WorkerMissionStatusModifier => {
	switch (status) {
		case WorkerMissionStatus.CONFIRMED:
			return WorkerMissionStatusModifier.BLUE;
		case WorkerMissionStatus.ACCEPTED:
			return WorkerMissionStatusModifier.GREEN;
		case WorkerMissionStatus.PROPOSED:
		case WorkerMissionStatus.REPROPOSED:
		case WorkerMissionStatus.REPROPOSED_BY_SMS:
			return WorkerMissionStatusModifier.YELLOW;
		case WorkerMissionStatus.REJECTED_BY_AGENCY:
		case WorkerMissionStatus.ABORTED:
			return WorkerMissionStatusModifier.RED;
		case WorkerMissionStatus.DECLINED:
		case WorkerMissionStatus.NOT_CONFIRMED:
			return WorkerMissionStatusModifier.GREY;
		default: {
			const exhaustiveCheck: never = status;
			return exhaustiveCheck;
		}
	}
};

export const workerMissionStatusToPlanningSidebarColor = (
	workerStatus: WorkerMissionStatus
): PlanningSidebarColor => {
	const modifier = getWorkerMissionStatusModifier(workerStatus);
	switch (modifier) {
		case WorkerMissionStatusModifier.GREEN:
			return PlanningSidebarColor.GREEN;
		case WorkerMissionStatusModifier.YELLOW:
			return PlanningSidebarColor.YELLOW;
		case WorkerMissionStatusModifier.RED:
		case WorkerMissionStatusModifier.GREY:
			return PlanningSidebarColor.GRAY;
		case WorkerMissionStatusModifier.BLUE:
			return PlanningSidebarColor.BLUE;
		default: {
			const exhaustiveCheck: never = modifier;
			return exhaustiveCheck;
		}
	}
};

const modifierToColor: Record<WorkerMissionStatusModifier, string> = {
	[WorkerMissionStatusModifier.BLUE]: blueBase,
	[WorkerMissionStatusModifier.GREEN]: green700,
	[WorkerMissionStatusModifier.YELLOW]: yellow700,
	[WorkerMissionStatusModifier.RED]: redBase,
	[WorkerMissionStatusModifier.GREY]: neutral900,
};

const modifierToTokenColor: Record<WorkerMissionStatusModifier, string> = {
	[WorkerMissionStatusModifier.BLUE]: 'var(--blue-base, #2051F8)',
	[WorkerMissionStatusModifier.GREEN]: 'var(--green-base, #00A355)',
	[WorkerMissionStatusModifier.YELLOW]: 'var(--yellow-base, #FAB305)',
	[WorkerMissionStatusModifier.RED]: 'var(--red-base, #E51940)',
	[WorkerMissionStatusModifier.GREY]: 'var(--icon-strong-900, #00061A)',
};

export const getWorkerMissionStatusColor = (
	status: WorkerMissionStatus
): string => modifierToColor[getWorkerMissionStatusModifier(status)];

export const getWorkerMissionStatusTokenColor = (
	status: WorkerMissionStatus
): string => modifierToTokenColor[getWorkerMissionStatusModifier(status)];
